import { IActiveSkill, IPassiveSkill } from "./ISkill";
import SkillRegistry from "./SkillRegistry";
import SkillStorage from "../Storage/SkillStorage";
import CharacterStorage from "../Storage/CharacterStorage";
import CharacterAttributes from "../Character/CharacterAttributes";
import { IArrowCaster } from "./IArrowCaster";
import { IManaConsumer } from "./IManaConsumer";

export interface ActiveSkillState {
  skill: IActiveSkill;
  level: number;
  cooldownRemaining: number;
}

export default class SkillManager {
  private mActiveSkills: ActiveSkillState[] = [];
  private mPassiveSkills: { skill: IPassiveSkill; level: number }[] = [];
  private mSkillStorage: SkillStorage;

  constructor(skillStorage: SkillStorage) {
    this.mSkillStorage = skillStorage;
  }

  public LoadAllocatedSkills(): void {
    this.mActiveSkills = [];
    this.mPassiveSkills = [];

    const allSkills = SkillRegistry.GetAllSkills();

    for (const skill of allSkills) {
      const level = this.mSkillStorage.GetSkillLevel(skill.Name);
      if (level <= 0) continue;

      if ("Execute" in skill) {
        this.mActiveSkills.push({
          skill: skill as IActiveSkill,
          level,
          cooldownRemaining: 0,
        });
      } else if ("ApplyBonus" in skill) {
        this.mPassiveSkills.push({
          skill: skill as IPassiveSkill,
          level,
        });
      }
    }
  }

  public ApplyPassives(attributes: CharacterAttributes): void {
    for (const { skill, level } of this.mPassiveSkills) {
      skill.ApplyBonus(level, attributes);
    }
  }

  public TryActivateSkill(
    keyBinding: string,
    caster: IArrowCaster,
    mana: IManaConsumer
  ): boolean {
    const state = this.mActiveSkills.find(
      (s) => s.skill.KeyBinding === keyBinding
    );
    if (!state) return false;
    if (state.cooldownRemaining > 0) return false;

    const manaCost = state.skill.ManaCost(state.level);
    if (!mana.ConsumeMana(manaCost)) return false;

    const success = state.skill.Execute(state.level, caster);
    if (success) {
      state.cooldownRemaining = state.skill.CooldownMs(state.level);
    }
    return success;
  }

  public AllocatePoint(
    skillName: string,
    charAttribs: CharacterAttributes,
    charStorage: CharacterStorage
  ): boolean {
    if (charAttribs.AvailableSkillPoints <= 0) return false;

    const skill = SkillRegistry.GetSkill(skillName);
    if (!skill) return false;

    const currentLevel = this.mSkillStorage.GetSkillLevel(skillName);
    if (currentLevel >= skill.MaxLevel) return false;

    const newLevel = currentLevel + 1;
    charAttribs.AvailableSkillPoints--;
    charStorage.SetAvailableSkillPoints(charAttribs.AvailableSkillPoints);
    this.mSkillStorage.SetSkillLevel(skillName, newLevel);
    this.mSkillStorage.AddAllocatedSkill(skillName);

    return true;
  }

  public UpdateCooldowns(deltaMs: number): void {
    for (const state of this.mActiveSkills) {
      if (state.cooldownRemaining > 0) {
        state.cooldownRemaining = Math.max(
          0,
          state.cooldownRemaining - deltaMs
        );
      }
    }
  }

  public GetActiveSkillStates(): ActiveSkillState[] {
    return this.mActiveSkills;
  }

  public GetSkillLevel(skillName: string): number {
    return this.mSkillStorage.GetSkillLevel(skillName);
  }
}
